/**
 * Whether a platform will still carry a free-form reply.
 *
 * Meta gives a Page 24 hours from the customer's last message to write back
 * with anything it likes. After that, a plain reply is refused outright —
 * "(#10) This message is sent outside of allowed window" — and the only ways
 * through are a message tag, which is limited to specific purposes such as an
 * update about an order already placed, or the customer writing again.
 *
 * The shop met this the hard way: three messages on one morning were typed,
 * sent, and rejected, and each had to be written a second time in the Messenger
 * app. The app knew each window had closed the day before — it stores the
 * expiry — and said nothing until after the send. This is what lets the thread
 * say so beforehand.
 */
/**
 * Channels that enforce a window on free-form replies.
 *
 * Messenger and Instagram are Meta's 24-hour standard messaging window.
 * WhatsApp has its own 24-hour customer service window, enforced the same way.
 * The rest are either the shop's own outbound channels or have no such rule.
 */
const WINDOWED = new Set(['messenger', 'instagram', 'whatsapp']);
export function replyWindow(channel, expiresAt, now = new Date()) {
    if (!WINDOWED.has(channel) || !expiresAt)
        return { state: 'not_applicable' };
    const expiry = Date.parse(expiresAt);
    if (Number.isNaN(expiry))
        return { state: 'not_applicable' };
    const msRemaining = expiry - now.getTime();
    return msRemaining > 0 ? { state: 'open', expiresAt, msRemaining } : { state: 'closed', expiresAt };
}
/**
 * Close to shutting, and worth a quiet word.
 *
 * Two hours, because a thread left until the end of the day is the one that
 * gets answered too late. Below this the composer mentions it; above it, it
 * says nothing rather than nagging.
 */
export const CLOSING_SOON_MS = 2 * 60 * 60 * 1000;
export function isClosingSoon(window) {
    return window.state === 'open' && window.msRemaining <= CLOSING_SOON_MS;
}
/**
 * Meta's allowance for a person answering a customer inquiry: 7 days from the
 * customer's last message, under the HUMAN_AGENT tag.
 *
 * This is what a bakery closed from Friday evening to Monday morning needs.
 * Sixty-three hours with nobody at the counter puts every weekend message
 * outside the 24-hour window before anyone can read it, and the reply on
 * Monday is not spam — it is the answer to a question the shop was asked.
 */
export const HUMAN_AGENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
/** Channels where the human-agent tag exists. WhatsApp uses templates instead. */
const HUMAN_AGENT_CHANNELS = new Set(['messenger', 'instagram']);
export function humanAgentState(channel, replyWindowExpiresAt, lastInboundAt, now = new Date()) {
    if (!HUMAN_AGENT_CHANNELS.has(channel))
        return 'not_needed';
    // Inside the ordinary window a plain reply is correct, and claiming the
    // allowance would be claiming something the message does not need.
    if (replyWindow(channel, replyWindowExpiresAt, now).state !== 'closed')
        return 'not_needed';
    // Nobody asked us anything, so there is no inquiry to answer.
    if (!lastInboundAt)
        return 'not_needed';
    const since = now.getTime() - Date.parse(lastInboundAt);
    if (Number.isNaN(since))
        return 'not_needed';
    return since <= HUMAN_AGENT_WINDOW_MS ? 'available' : 'too_old';
}
//# sourceMappingURL=reply-window.js.map